import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { IListItem } from 'types/Contract';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChapterLabel } from '../../WorkOrder/types';
import { bimWorkOrderVersionChapterPriceCountsGql } from '../workOrderVersionQueries';

export const useBimWorkOrderPriceVersionCounter = ({
    workOrderVersionId,
    workOrderSectionOrChapter,
    constructionTypeId,
    workOrderVersionRootObjectId,
}: {
    workOrderVersionId: number | undefined;
    workOrderSectionOrChapter: IListItem<string> | undefined;
    workOrderVersionRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
}) => {
    const isValidToDisplay = workOrderVersionId && constructionTypeId && workOrderVersionRootObjectId;
    const dispatch = useDispatch();

    const [bimWorkOrderVersionChapterData, , , bimWorkOrderChapterDataRequest] = useRequest({
        requestId: 'bimWorkOrderVersionChapterPriceCounts',
        query: bimWorkOrderVersionChapterPriceCountsGql,
        withEffect: false,
    });

    const [resetBimPriceListCounterData] = useResetStoreData({
        requestId: 'bimWorkOrderVersionChapterPriceCounts',
    });

    const makeRequestPriceListData = useCallback(() => {
        dispatch(
            bimWorkOrderChapterDataRequest({ workOrderVersionId, constructionTypeId, workOrderVersionRootObjectId }),
        );
    }, [
        bimWorkOrderChapterDataRequest,
        workOrderVersionRootObjectId,
        constructionTypeId,
        dispatch,
        workOrderVersionId,
    ]);

    useEffect(() => {
        if (isValidToDisplay) {
            makeRequestPriceListData();
        }
    }, [
        workOrderVersionRootObjectId,
        constructionTypeId,
        isValidToDisplay,
        makeRequestPriceListData,
        workOrderVersionId,
    ]);

    const priceListVersionCounter = useMemo(
        () =>
            isValidToDisplay
                ? bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data?.find(
                      (el) => el?.chapter === workOrderSectionOrChapter?.id,
                  )?.pricesCount
                : undefined,
        [
            bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data,
            isValidToDisplay,
            workOrderSectionOrChapter?.id,
        ],
    );

    const chaptersTooltip = useMemo(() => {
        if (!bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data || !isValidToDisplay) {
            return undefined;
        }

        return bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data.map((el) => ({
            chapter: el?.chapter ? bimWorkOrderChapterLabel[el.chapter] : null,
            priceCount: el?.pricesCount,
        }));
    }, [bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data, isValidToDisplay]);

    const workOrderHasNoPrices = useMemo(
        () =>
            bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data?.every(
                (chapter) => chapter?.pricesCount === 0,
            ),
        [bimWorkOrderVersionChapterData?.bimWorkOrderVersionChapterPriceCounts?.data],
    );

    return {
        priceListVersionCounter,
        chaptersTooltip,
        resetBimPriceListCounterData,
        makeRequestPriceListData,
        workOrderHasNoPrices,
    };
};
